'use client';

import * as React from 'react';

import { LSPClient, languageServerExtensions } from '@codemirror/lsp-client';
import type { Extension } from '@codemirror/state';
import type { JSONSchema7 } from 'json-schema';

import type { LspContext } from './types';
import { WorkerTransport } from './workerTransport';

/**
 * Context value provided by LspProvider
 */
export interface LspContextValue {
  /**
   * Update the LSP context with a new schema.
   * Call this when the selected node changes or when scope variables change.
   */
  updateContext: (context: LspContext) => void;

  /**
   * Get CodeMirror extensions for a document.
   * Pass the returned extensions to the CodeMirror editor.
   *
   * @param documentUri - Unique URI for this document (e.g., "workflow://automation/node-id")
   * @param languageId - Optional language ID (e.g., "expr", "typescript"). If not provided,
   *                     defaults to deriving from CodeMirror's language configuration.
   * @returns CodeMirror extensions for LSP features
   */
  getExtensions: (documentUri: string, languageId?: string) => Extension;

  /**
   * Whether the LSP client is ready
   */
  isReady: boolean;
}

const LspReactContext = React.createContext<LspContextValue | null>(null);

/**
 * Props for LspProvider
 */
export interface LspProviderProps {
  children: React.ReactNode;
  /**
   * URL or factory function to create the Web Worker.
   * If not provided, defaults to the built-in expr language server.
   *
   * @example
   * ```tsx
   * // Using a custom worker URL
   * <LspProvider workerUrl={new URL('./myLsp.worker.ts', import.meta.url)}>
   *
   * // Using a factory function
   * <LspProvider workerUrl={() => new Worker(new URL('./myLsp.worker.ts', import.meta.url), { type: 'module' })}>
   * ```
   */
  workerUrl?: URL | (() => Worker);
}

/**
 * Language-agnostic LSP provider component.
 *
 * Manages the LSP client lifecycle and provides context for updating
 * workflow scope information. The workflow can pass context (like JSON Schema)
 * to any LSP server, regardless of the target language.
 *
 * @example
 * ```tsx
 * // Using the default expr language server
 * <LspProvider>
 *   <YourEditorComponent />
 * </LspProvider>
 *
 * // Using a custom language server
 * <LspProvider workerUrl={new URL('./myLanguage.worker.ts', import.meta.url)}>
 *   <YourEditorComponent />
 * </LspProvider>
 * ```
 */
export function LspProvider({ children, workerUrl }: LspProviderProps) {
  const [isReady, setIsReady] = React.useState(false);
  const transportRef = React.useRef<WorkerTransport | null>(null);
  const clientRef = React.useRef<LSPClient | null>(null);
  const workerRef = React.useRef<Worker | null>(null);
  // Cache extensions per document URI to ensure stable references
  const extensionsCacheRef = React.useRef<Map<string, Extension>>(new Map());

  // Initialize worker and client on mount
  React.useEffect(() => {
    console.log('[LSP-DEBUG] LspProvider: Initializing...');

    // Create worker based on provided URL or factory
    let worker: Worker;
    if (typeof workerUrl === 'function') {
      console.log(
        '[LSP-DEBUG] LspProvider: Creating worker from factory function',
      );
      worker = workerUrl();
    } else if (workerUrl) {
      console.log(
        '[LSP-DEBUG] LspProvider: Creating worker from URL:',
        workerUrl.toString(),
      );
      worker = new Worker(workerUrl, { type: 'module' });
    } else {
      // Default to the expr language server
      console.log('[LSP-DEBUG] LspProvider: Creating default expr LSP worker');
      worker = new Worker(new URL('./exprLsp.worker.ts', import.meta.url), {
        type: 'module',
      });
    }
    workerRef.current = worker;

    // Create transport
    const transport = new WorkerTransport(worker);
    transportRef.current = transport;

    // Wait for worker to be ready, then connect
    console.log('[LSP-DEBUG] LspProvider: Waiting for worker to be ready...');
    transport.waitForReady().then(() => {
      console.log('[LSP-DEBUG] LspProvider: Worker ready, creating LSP client');
      // Create LSP client with extensions
      const client = new LSPClient({
        extensions: languageServerExtensions(),
      });

      // Store client ref immediately so it's available
      clientRef.current = client;

      console.log(
        '[LSP-DEBUG] LspProvider: LSP client created, connecting to transport...',
      );
      client.connect(transport);
      console.log(
        '[LSP-DEBUG] LspProvider: connect() called, waiting for initialization...',
      );

      // Wait for the client to be fully initialized before setting isReady
      // This ensures serverCapabilities are available when we start using the client
      client.initializing
        .then(() => {
          console.log(
            '[LSP-DEBUG] LspProvider: LSP client fully initialized, setting isReady=true',
          );
          setIsReady(true);
        })
        .catch((err) => {
          console.error(
            '[LSP-DEBUG] LspProvider: LSP initialization failed:',
            err,
          );
        });
    });

    // Cleanup on unmount
    return () => {
      worker.terminate();
      workerRef.current = null;
      transportRef.current = null;
      clientRef.current = null;
      extensionsCacheRef.current.clear();
      setIsReady(false);
    };
  }, [workerUrl]);

  const updateContext = React.useCallback((context: LspContext) => {
    console.log('[LSP-DEBUG] LspProvider.updateContext called:', {
      documentUri: context.documentUri,
      schemaProperties: context.schema?.properties
        ? Object.keys(context.schema.properties)
        : 'none',
    });
    transportRef.current?.updateContext(context);
  }, []);

  const getExtensions = React.useCallback(
    (documentUri: string, languageId?: string): Extension => {
      console.log('[LSP-DEBUG] LspProvider.getExtensions called:', {
        documentUri,
        languageId,
        clientReady: !!clientRef.current,
        isReady,
      });
      if (!clientRef.current) {
        // Return empty extension if not ready
        console.log(
          '[LSP-DEBUG] LspProvider.getExtensions: Client not ready, returning empty',
        );
        return [];
      }

      // Create a cache key from documentUri and languageId
      const cacheKey = `${documentUri}::${languageId || ''}`;

      // Check cache first to return stable extension references
      const cached = extensionsCacheRef.current.get(cacheKey);
      if (cached) {
        console.log(
          '[LSP-DEBUG] LspProvider.getExtensions: Returning cached extension for:',
          cacheKey,
        );
        return cached;
      }

      // Create new extension and cache it
      const ext = clientRef.current.plugin(documentUri, languageId);
      extensionsCacheRef.current.set(cacheKey, ext);
      console.log(
        '[LSP-DEBUG] LspProvider.getExtensions: Created and cached plugin:',
        cacheKey,
        ext,
      );
      return ext;
    },
    [isReady], // Re-create when ready state changes
  );

  const contextValue = React.useMemo<LspContextValue>(
    () => ({
      updateContext,
      getExtensions,
      isReady,
    }),
    [updateContext, getExtensions, isReady],
  );

  return (
    <LspReactContext.Provider value={contextValue}>
      {children}
    </LspReactContext.Provider>
  );
}

/**
 * Hook to access the LSP context.
 *
 * Must be used within an LspProvider.
 *
 * @example
 * ```tsx
 * function MyEditor() {
 *   const { updateContext, getExtensions, isReady } = useLsp();
 *
 *   useEffect(() => {
 *     updateContext({
 *       schema: mySchema,
 *       documentUri: 'workflow://my-document',
 *     });
 *   }, [mySchema, updateContext]);
 *
 *   const extensions = useMemo(
 *     () => getExtensions('workflow://my-document'),
 *     [getExtensions]
 *   );
 *
 *   return <CodeEditor extensions={extensions} />;
 * }
 * ```
 */
export function useLsp(): LspContextValue {
  const context = React.useContext(LspReactContext);

  if (!context) {
    throw new Error('useLsp must be used within an LspProvider');
  }

  return context;
}

/**
 * Convenience function to create a combined schema from scope variables.
 * Each key in the record becomes a top-level property in the schema.
 *
 * @example
 * ```ts
 * const schema = createScopeSchema({
 *   trigger: triggerSchema,
 *   data: entitySchema,
 *   action1: action1OutputSchema,
 * });
 * ```
 */
export function createScopeSchema(
  scopes: Record<string, JSONSchema7>,
): JSONSchema7 {
  return {
    type: 'object',
    properties: scopes,
  };
}
